<template>
    <div class="navbar__top" data-testid="navbar-top-modes">
        <ul v-if="hasMultipleModes" class="navbar__top-modes">
            <li v-for="availableMode in moduleConfig.modes" :key="availableMode.id" name="navbar-mode-tab">
                <NuxtLink
                    :to="$withLocalePrefix(availableMode.page)"
                    :class="{active: selectedMode === availableMode.id}"
                    :data-testid="`navbar-mode-tab-${availableMode.id}`"
                    @click="switchMode(availableMode.id)"
                >
                    <div class="navbar__top-modes-icon">
                        <VJoyIcon :name="availableMode.id === 'client' ? 'company-placeholder' : 'super-malter'" color="neutral-50" />
                        {{ $t(`navbar.top.${availableMode.id}.label` as keyof AllTranslationsRecord) }}
                    </div>
                </NuxtLink>
            </li>
        </ul>
        <!-- TODO: implement language selection or use existing one -->
        <div class="navbar__top-language-selector">
            <button class="navbar__top-language-button" @click="toggleLanguagePopup">English</button>
            <div v-if="isLanguagePopupOpen" class="navbar__top-language-popup">
                <button v-for="language in languages" :key="language.code" class="navbar__top-language-option" @click="selectLanguage(language)">
                    {{ language.name }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import {useLogger} from '#imports';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {useNavbarMode} from '../composables/useNavbarMode';
    import type {AllTranslationsRecord} from '#build/i18n';

    const {config: moduleConfig, hasMultipleModes, selectedMode, switchMode} = useNavbarMode();

    const {isLanguagePopupOpen, languages, toggleLanguagePopup, selectLanguage} = managageLanguagePopup();

    function managageLanguagePopup() {
        const isLanguagePopupOpen = ref(false);

        // TODO: do not keep hardcoded languages
        const languages = [
            {code: 'en', name: 'English'},
            {code: 'fr', name: 'Français'},
            {code: 'es', name: 'Español'},
            {code: 'de', name: 'Deutsch'},
            {code: 'it', name: 'Italiano'},
            {code: 'pt', name: 'Português'},
        ];

        const toggleLanguagePopup = () => {
            isLanguagePopupOpen.value = !isLanguagePopupOpen.value;
        };

        // TODO: implement language selection
        const logger = useLogger();
        const selectLanguage = (language: (typeof languages)[0]) => {
            logger.info('Selected language:', language);
            isLanguagePopupOpen.value = false;
        };

        return {
            isLanguagePopupOpen,
            languages,
            toggleLanguagePopup,
            selectLanguage,
        };
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    /* mobile-first: keep host in DOM (height 0) so modes can be repositioned into the drawer */

    .header-unlogged:has(.navbar__content--open):not(:has(.navbar__submenu--open)):has(.navbar__top-modes) {
        --navbar-modes-mobile-offset: var(--navbar-height);

        .navbar__top .navbar__top-modes {
            visibility: visible;
            opacity: 1;
            transform: translateY(0);
        }
    }

    .navbar__top {
        display: flex;
        align-items: center;
        height: 0;
        padding: 0;
        margin: 0;
        gap: var(--joy-core-spacing-2);
        overflow: visible;

        &-modes {
            display: flex;
            position: fixed;
            list-style-type: none;
            gap: var(--joy-core-spacing-2);
            top: var(--navbar-height);
            left: 0;
            width: 100%;
            z-index: 2;
            justify-content: center;
            margin-top: var(--joy-core-spacing-3);
            padding-bottom: var(--joy-core-spacing-4);
            border-bottom: 1px solid var(--joy-color-neutral-30);
            opacity: 0;
            visibility: hidden;
            transform: translateY(-20px);
            transition: all var(--navbar-transition-duration) var(--navbar-transition-easing);

            &-icon {
                display: flex;
                align-items: center;
                gap: var(--joy-core-spacing-2);
            }
        }

        li {
            display: inline-block;

            a {
                @include text.getFontBySize(primary-450);
                display: block;
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-6);
                color: var(--joy-color-neutral-70);
                text-decoration: none;
                border-radius: var(--joy-core-spacing-6);
                transition: all calc(var(--navbar-transition-duration) * 0.67) var(--navbar-transition-easing);
                background-color: var(--joy-color-neutral-20);

                &:hover,
                &.active {
                    background-color: var(--joy-color-neutral-30);
                }
            }
        }

        &-language-selector {
            display: none;
        }

        // TO BE REMOVED IF WE USE OLD LANGUAGE SELECTOR
        &-language-button {
            display: block;
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-4);
            color: #555;
            background: none;
            border: none;
            font-weight: 500;
            transition: all calc(var(--navbar-transition-duration) * 0.67) var(--navbar-transition-easing);
            border-radius: var(--joy-core-radius-3);
            cursor: pointer;
            font-size: inherit;
            font-family: inherit;

            &:hover {
                background-color: #eee;
                color: #000;
            }
        }

        &-language-popup {
            position: absolute;
            top: 100%;
            right: 0;
            margin-top: var(--joy-core-spacing-2);
            background: var(--joy-color-neutral-0);
            border-radius: var(--joy-core-radius-3);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            padding: var(--joy-core-spacing-2);
            min-width: 150px;
            z-index: 1000;
        }

        &-language-option {
            display: block;
            width: 100%;
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-4);
            color: #555;
            background: none;
            border: none;
            font-weight: 500;
            transition: all calc(var(--navbar-transition-duration) * 0.67) var(--navbar-transition-easing);
            border-radius: var(--joy-core-radius-3);
            cursor: pointer;
            font-size: inherit;
            font-family: inherit;
            text-align: left;

            &:hover {
                background-color: #eee;
                color: #000;
            }
        }
    }

    /* desktop */
    @include mq.screen_sm(min) {
        .navbar__top {
            height: var(--topnav-height);

            &-modes {
                display: flex;
                position: static;
                width: auto;
                margin: 0;
                padding: 0;
                border-bottom: none;
                background-color: transparent;
                justify-content: flex-start;
                transform: translateY(0);
                transition: none;
                visibility: visible;
                opacity: 1;
            }

            &-language-selector {
                display: block;
                margin-left: auto;
                position: relative;
            }
        }
    }
</style>
